import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { 
  AutocompleteOption, 
  AutocompleteGroup, 
  AutocompleteApiResponse,
} from '../models/ni-autocomplete.model';

/**
 * Options for input validation
 */
export interface ValidateInputOptions {
  inputPattern?: string;
  allowOnlyNumbers?: boolean;
  allowOnlyLetters?: boolean;
  allowOnlyAlphanumeric?: boolean;
  allowOnlyUppercase?: boolean;
  allowOnlyLowercase?: boolean;
  inputType?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NiAutocompleteService {
  private readonly searchTerm$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly http: HttpClient) {}

  /**
   * Setup API search with debouncing
   */
  setupApiSearch(
    apiUrl: string,
    delay: number = 300,
    minLength: number = 1,
    apiParams: any = {},
    pageSize: number = 20
  ): Observable<{ results: AutocompleteOption[], totalItems: number }> {
    return this.searchTerm$.pipe(
      debounceTime(delay),
      distinctUntilChanged(),
      switchMap((term: string) => this.searchApi(term, apiUrl, minLength, apiParams, pageSize))
    );
  }

  /**
   * Search API with parameters
   */
  private searchApi(
    term: string, 
    apiUrl: string, 
    minLength: number, 
    apiParams: any, 
    pageSize: number
  ): Observable<{ results: AutocompleteOption[], totalItems: number }> {
    if (!term || term.length < minLength) {
      return of({ results: [], totalItems: 0 });
    }

    const params = {
      ...apiParams,
      query: term,
      pageSize
    };

    return this.http.get<AutocompleteApiResponse>(apiUrl, { params }).pipe(
      switchMap(response => {
        const totalItems = response.totalItems || response.total || response.count || response.length || 0;
        return of({ 
          results: response.data || response, 
          totalItems 
        });
      })
    );
  }

  /**
   * Filter local options
   */
  filterLocalOptions(
    options: AutocompleteOption[], 
    query: string, 
    optionLabel: string = 'name'
  ): AutocompleteOption[] {
    if (!query) return options;
    
    return options.filter(opt => 
      String(opt[optionLabel] || '').toLowerCase().includes(query.toLowerCase())
    );
  }

  /**
   * Group options by field
   */
  groupOptions(
    options: AutocompleteOption[], 
    groupField: string = 'group'
  ): AutocompleteGroup[] {
    const groups: { [key: string]: AutocompleteOption[] } = {};
    
    options.forEach(option => {
      const groupKey = option[groupField] || 'default';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(option);
    });

    return Object.keys(groups).map(key => ({
      label: key === 'default' ? 'Other' : key,
      value: key,
      items: groups[key]
    }));
  }

  /**
   * Get option label
   */
  getOptionLabel(option: AutocompleteOption, optionLabel: string = 'name'): string {
    if (!option) return '';
    
    // Try the specified label field first
    const labelValue = option[optionLabel];
    if (labelValue != null && labelValue !== '') {
      return String(labelValue);
    }
    
    // Fallback to standard 'label' field
    if (option.label != null && option.label !== '') {
      return String(option.label);
    }
    
    // Fallback to 'value' if it's a primitive
    if (option.value != null && (typeof option.value === 'string' || typeof option.value === 'number')) {
      return String(option.value);
    }
    
    return '';
  }

  /**
   * Check if option is selected
   */
  isOptionSelected(
    option: AutocompleteOption, 
    selected: any, 
    multiple: boolean, 
    valueField: string = 'value'
  ): boolean {
    if (multiple) {
      if (!Array.isArray(selected)) return false;
      return selected.some(item => 
        item === option[valueField] || 
        (typeof item === 'object' && item[valueField] === option[valueField])
      );
    }
    
    if (typeof selected === 'object' && selected) {
      return selected[valueField] === option[valueField];
    }
    
    return selected === option[valueField];
  }

  /**
   * Validate input based on patterns and rules
   */
  validateInput(
    value: string,
    options: ValidateInputOptions = {}
  ): string {
    const {
      inputPattern,
      allowOnlyNumbers,
      allowOnlyLetters,
      allowOnlyAlphanumeric,
      allowOnlyUppercase,
      allowOnlyLowercase,
      inputType = 'text'
    } = options;

    let validatedValue = value;
    
    // Apply regex pattern validation
    if (inputPattern) {
      const regex = new RegExp(inputPattern);
      validatedValue = validatedValue.split('').filter(char => regex.test(char)).join('');
    }
    
    // Apply type-specific validations
    if (allowOnlyNumbers) {
      validatedValue = validatedValue.replace(/\D/g, '');
    } else if (allowOnlyLetters) {
      validatedValue = validatedValue.replace(/[^a-zA-Z\s]/g, '');
    } else if (allowOnlyAlphanumeric) {
      validatedValue = validatedValue.replace(/[^a-zA-Z0-9\s]/g, '');
    }
    
    // Apply case transformations
    if (allowOnlyUppercase) {
      validatedValue = validatedValue.toUpperCase();
    } else if (allowOnlyLowercase) {
      validatedValue = validatedValue.toLowerCase();
    }
    
    // Apply input type specific validations
    switch (inputType) {
      case 'number':
        validatedValue = validatedValue.replace(/[^0-9.-]/g, '');
        break;
      case 'email':
        validatedValue = validatedValue.replace(/[^a-zA-Z0-9@._-]/g, '');
        break;
      case 'tel':
        validatedValue = validatedValue.replace(/[^0-9\s\-()+]/g, '');
        break;
      case 'url':
        validatedValue = validatedValue.replace(/[^a-zA-Z0-9:/._-]/g, '');
        break;
    }
    
    return validatedValue;
  }

  /**
   * Calculate visible chips based on container width
   */
  calculateVisibleChips(
    selectedOptions: AutocompleteOption[],
    containerWidth: number,
    chipWidths: number[],
    overflowIndicatorWidth: number = 40
  ): { visibleCount: number, hiddenCount: number } {
    if (!selectedOptions.length) {
      return { visibleCount: 0, hiddenCount: 0 };
    }

    let currentWidth = 0;
    let visibleCount = 0;

    for (const chipWidth of chipWidths) {
      if (currentWidth + chipWidth > containerWidth - overflowIndicatorWidth) {
        break;
      }
      currentWidth += chipWidth;
      visibleCount++;
    }

    return {
      visibleCount,
      hiddenCount: Math.max(0, selectedOptions.length - visibleCount)
    };
  }

  /**
   * Get visible chips
   */
  getVisibleChips(
    selectedOptions: AutocompleteOption[], 
    visibleCount: number
  ): AutocompleteOption[] {
    return selectedOptions.slice(0, visibleCount);
  }

  /**
   * Get hidden chips count
   */
  getHiddenChipsCount(
    selectedOptions: AutocompleteOption[], 
    visibleCount: number
  ): number {
    return Math.max(0, selectedOptions.length - visibleCount);
  }

  /**
   * Emit search term
   */
  emitSearchTerm(term: string): void {
    this.searchTerm$.next(term);
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
} 